// État : Compte de résultat structuré (lecture seule, temps réel).
// Deux colonnes — CHARGES (classe 6) à gauche, PRODUITS (classe 7) à droite —
// avec sections (exploitation / financier / exceptionnel), résultats
// intermédiaires entre les colonnes et résultat net encadré en bas.
// Contrôle de cohérence avec le résultat repris au bilan.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { telechargerExport } from "@/lib/exportFichier";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";
import { TrendingUp, Download, Printer, Loader2, FileText, AlertTriangle } from "lucide-react";

const anneeCourante = new Date().getFullYear();
const fmt = (n) => Math.round(Math.abs(n || 0)).toLocaleString("fr-FR");
// Montant signé : les valeurs négatives sont affichées entre parenthèses
const signe = (n) => (Math.round(n || 0) < 0 ? `(${fmt(n)})` : fmt(n));

// Bloc d'une section (titre + lignes de comptes + sous-total)
function Bloc({ titre, lignes, sousTotal, libelleST, tousComptes, testid }) {
  const visibles = tousComptes ? lignes : lignes.filter((l) => l.montant !== 0);
  return (
    <div data-testid={testid} className="bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden">
      <div className="bg-[#E8F4F4] text-[#1D3D3D] px-3 py-1.5 font-bold text-xs uppercase tracking-wide">{titre}</div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-[#4A8A8A]/10">
          {visibles.length === 0 ? (
            <tr><td className="px-3 py-2 text-[#1D3D3D]/40 text-xs" colSpan={3}>Aucun mouvement.</td></tr>
          ) : visibles.map((l) => (
            <tr key={l.numero} className="hover:bg-[#E8F4F4]/40">
              <td className="px-3 py-1.5 font-mono text-[#1D3D3D]/60 text-xs whitespace-nowrap w-16">{l.numero}</td>
              <td className="px-3 py-1.5 text-[#1D3D3D]">{l.libelle}</td>
              <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{signe(l.montant)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-[#E8F4F4]/70 font-bold text-[#1D3D3D]">
            <td className="px-3 py-1.5 text-xs" colSpan={2}>{libelleST}</td>
            <td className="px-3 py-1.5 text-right tabular-nums" data-testid={`${testid}-st`}>{signe(sousTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Bandeau de résultat intermédiaire (centré entre les colonnes)
function ResultatInter({ libelle, montant, testid }) {
  const pos = Math.round(montant || 0) >= 0;
  return (
    <div data-testid={testid}
      className={`rounded-lg px-4 py-2.5 flex items-center justify-between font-bold text-sm ${pos
        ? "bg-[#2D5A5A] text-white" : "bg-red-50 text-red-800 border border-red-200"}`}>
      <span>{libelle}</span>
      <span className="tabular-nums" data-testid={`${testid}-montant`}>
        {pos ? "" : "− "}{fmt(montant)} XPF
      </span>
    </div>
  );
}

export default function CompteResultat() {
  const { company } = useAuth();
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [tousComptes, setTousComptes] = useState(false);
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const params = useCallback(() => ({
    exercice: parseInt(exercice, 10),
    date_debut: dateDebut || undefined,
    date_fin: dateFin || undefined,
  }), [exercice, dateDebut, dateFin]);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get("/etats/compte-resultat", { params: params() });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [params]);

  useEffect(() => { charger(); }, [charger]);

  const exporter = async (format) => {
    try {
      await telechargerExport("/etats/compte-resultat/export", params(), format, `compte_resultat_${exercice}`);
    } catch {
      toast.error("L'export a échoué. Veuillez réessayer.");
    }
  };

  const editionLe = new Date().toLocaleDateString("fr-FR");
  const periodeTexte = `du ${dateDebut || "début d'exercice"} au ${dateFin || "fin d'exercice"}`;
  const p = data?.produits;
  const ch = data?.charges;

  return (
    <div className="fenua-fade-up" data-testid="page-compte-resultat">
      <div className="no-print flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
            <TrendingUp className="h-6 w-6 text-[#1D3D3D]" />
          </div>
          <div>
            <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Compte de résultat</h1>
            <p className="text-sm text-[#1D3D3D]/70">Charges et produits par nature — résultat de l'exercice.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" data-testid="export-csv" onClick={() => exporter("csv")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="secondary" data-testid="export-xls" onClick={() => exporter("xls")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <Download className="h-4 w-4 mr-1" /> XLS
          </Button>
          <Button variant="secondary" data-testid="export-pdf" onClick={() => exporter("pdf")}
            className="bg-[#E8F4F4] text-[#1D3D3D] hover:bg-[#4A8A8A]/20 border border-[#4A8A8A]/20">
            <FileText className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button data-testid="bouton-imprimer" onClick={() => window.print()}
            className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
            <Printer className="h-4 w-4 mr-1" /> Imprimer
          </Button>
        </div>
      </div>

      {/* Filtres */}
      <div className="no-print mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="filtre-exercice" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Du</Label>
          <DatePicker testId="filtre-date-debut" value={dateDebut} onChange={setDateDebut} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Au</Label>
          <DatePicker testId="filtre-date-fin" value={dateFin} onChange={setDateFin} />
        </div>
        <div className="flex items-center gap-2 pb-1">
          <Switch data-testid="toggle-tous-comptes" checked={tousComptes} onCheckedChange={setTousComptes} />
          <Label className="text-xs font-medium text-[#1D3D3D] cursor-pointer">Afficher tous les comptes</Label>
        </div>
      </div>

      {/* Contenu imprimable */}
      <div className="mt-6 zone-impression">
        <div className="bg-white border border-[#4A8A8A]/20 rounded-lg px-5 py-3 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-1">
            <p className="font-bold text-[#1D3D3D]">{company?.raison_sociale}
              <span className="font-normal text-xs text-[#1D3D3D]/60"> · N° Tahiti {company?.numero_tahiti}</span></p>
            <p className="text-xs text-[#1D3D3D]/60">Édité le {editionLe}</p>
          </div>
          <p className="text-xs text-[#1D3D3D]/60">Compte de résultat — Exercice {exercice} · Période {periodeTexte}</p>
        </div>

        {chargement ? (
          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : !data ? (
          <p className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 text-center text-sm text-[#1D3D3D]/60">Aucune donnée pour ces critères.</p>
        ) : (
          <div className="space-y-3">
            {!data.coherent && (
              <div data-testid="alerte-coherence"
                className="rounded-lg px-4 py-3 flex items-center gap-3 bg-red-50 border border-red-200 text-red-800 text-sm font-semibold">
                <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
                Attention : le résultat du compte de résultat ({signe(data.resultat_net)} XPF) diffère du
                résultat repris dans le bilan ({signe(data.resultat_bilan)} XPF). Vérifiez vos écritures.
              </div>
            )}

            {/* En-têtes de colonnes */}
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-[#8A4A4A] text-white text-center font-bold text-sm py-2 rounded">CHARGES (dépenses)</div>
              <div className="bg-[#2D5A5A] text-white text-center font-bold text-sm py-2 rounded">PRODUITS (recettes)</div>
            </div>

            {/* Exploitation */}
            <div className="grid md:grid-cols-2 gap-3">
              <Bloc testid="charges-exploitation" titre="Charges d'exploitation (IV)" lignes={ch.exploitation}
                sousTotal={ch.st_exploitation} libelleST="Total charges d'exploitation (IV)" tousComptes={tousComptes} />
              <Bloc testid="produits-exploitation" titre="Produits d'exploitation (I)" lignes={p.exploitation}
                sousTotal={p.st_exploitation} libelleST="Total produits d'exploitation (I)" tousComptes={tousComptes} />
            </div>
            <ResultatInter testid="resultat-exploitation" libelle="RÉSULTAT D'EXPLOITATION (I − IV)" montant={data.resultat_exploitation} />

            {/* Financier */}
            <div className="grid md:grid-cols-2 gap-3">
              <Bloc testid="charges-financier" titre="Charges financières (V)" lignes={ch.financier}
                sousTotal={ch.st_financier} libelleST="Total charges financières (V)" tousComptes={tousComptes} />
              <Bloc testid="produits-financier" titre="Produits financiers (II)" lignes={p.financier}
                sousTotal={p.st_financier} libelleST="Total produits financiers (II)" tousComptes={tousComptes} />
            </div>
            <ResultatInter testid="resultat-financier" libelle="RÉSULTAT FINANCIER (II − V)" montant={data.resultat_financier} />

            {/* Exceptionnel */}
            <div className="grid md:grid-cols-2 gap-3">
              <Bloc testid="charges-exceptionnel" titre="Charges exceptionnelles (VI)" lignes={ch.exceptionnel}
                sousTotal={ch.st_exceptionnel} libelleST="Total charges exceptionnelles (VI)" tousComptes={tousComptes} />
              <Bloc testid="produits-exceptionnel" titre="Produits exceptionnels (III)" lignes={p.exceptionnel}
                sousTotal={p.st_exceptionnel} libelleST="Total produits exceptionnels (III)" tousComptes={tousComptes} />
            </div>
            <ResultatInter testid="resultat-exceptionnel" libelle="RÉSULTAT EXCEPTIONNEL (III − VI)" montant={data.resultat_exceptionnel} />

            {/* Participation & impôts (le cas échéant) */}
            {(ch.participation.length > 0 || ch.impots.length > 0) && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-3">
                  {ch.participation.length > 0 && (
                    <Bloc testid="charges-participation" titre="Participation des salariés" lignes={ch.participation}
                      sousTotal={ch.st_participation} libelleST="Total participation" tousComptes={tousComptes} />
                  )}
                  {ch.impots.length > 0 && (
                    <Bloc testid="charges-impots" titre="Impôts sur les bénéfices" lignes={ch.impots}
                      sousTotal={ch.st_impots} libelleST="Total impôts sur les bénéfices" tousComptes={tousComptes} />
                  )}
                </div>
                <div />
              </div>
            )}

            {/* Totaux généraux */}
            <div className="grid md:grid-cols-2 gap-3">
              <div data-testid="total-charges" className="bg-[#8A4A4A] text-white rounded-lg px-4 py-3 flex items-center justify-between font-bold">
                <span>TOTAL DES CHARGES</span><span className="tabular-nums">{signe(ch.total)} XPF</span>
              </div>
              <div data-testid="total-produits" className="bg-[#2D5A5A] text-white rounded-lg px-4 py-3 flex items-center justify-between font-bold">
                <span>TOTAL DES PRODUITS</span><span className="tabular-nums">{signe(p.total)} XPF</span>
              </div>
            </div>

            {/* Résultat net encadré — libellé permanent + badge dynamique */}
            {(() => {
              const r = Math.round(data.resultat_net || 0);
              const positif = r > 0, negatif = r < 0;
              const badge = positif
                ? { libelle: "Bénéfice", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", testid: "badge-benefice" }
                : negatif
                  ? { libelle: "Perte", cls: "bg-red-100 text-red-700 border-red-300", testid: "badge-perte" }
                  : { libelle: "Résultat nul", cls: "bg-gray-100 text-gray-600 border-gray-300", testid: "badge-resultat-nul" };
              return (
                <div data-testid="bandeau-resultat"
                  className={`rounded-xl px-6 py-5 flex flex-wrap items-center justify-between gap-4 border-2 ${negatif
                    ? "bg-red-50 border-red-400 text-red-800" : "bg-[#E8F4F4] border-[#2D5A5A] text-[#1D3D3D]"}`}>
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="text-base font-black">RÉSULTAT NET DE L'EXERCICE</p>
                      <span data-testid={badge.testid} title={badge.libelle}
                        className={`text-xs font-bold px-2.5 py-0.5 rounded-full border ${badge.cls}`}>{badge.libelle}</span>
                    </div>
                    <p className="text-xs opacity-70">(I + II + III) − (IV + V + VI) − participation − impôts</p>
                  </div>
                  <p data-testid="resultat-montant"
                    className={`text-3xl font-black tabular-nums ${negatif ? "text-red-700" : ""}`}>
                    {negatif ? "(" : ""}{fmt(r)}{negatif ? ")" : ""} XPF
                  </p>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
